import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { JsonView, darkStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';

import MDBox from 'components/MDBox';
import MDBadge from 'components/MDBadge';
import MDTypography from 'components/MDTypography';
import { ItemIdentified } from 'components/ItemIdentified';
import DataTable from 'components/Tables/DataTable';
import { formatDate, isArray } from '../lib/utils';

function LogDataTable({ getData = () => {}, reupdate = 'initial', onupdate = () => {} }) {
  const [data, setData] = useState({ rows: [], columns: [], fetching: true });

  const mappingLevel = (l) => {
    const map = {
      CRITICAL: 'error',
      ALERT: 'error',
      EMERGENCY: 'error',
      ERROR: 'error',
      WARNING: 'warning',
      NOTICE: 'dark',
      INFO: 'info',
      DEBUG: 'secondary',
    };
    return map[l] ?? '-';
  };

  const mappingSortLevel = (l) => {
    const map = {
      CRITICAL: 1,
      ALERT: 2,
      EMERGENCY: 3,
      ERROR: 4,
      WARNING: 5,
      NOTICE: 6,
      INFO: 7,
      DEBUG: 8,
    };
    return map[l] ?? 8;
  };

  const sortLevelFn = useMemo(() => {
    return (rowA, rowB, columnId, desc) => {
      const valueA = mappingSortLevel(rowA.original.level);
      const valueB = mappingSortLevel(rowB.original.level);

      // Restituisci il confronto per ordinamento (valore più basso è più critico)
      if (valueA < valueB) return -1; // levelA è più critico
      if (valueA > valueB) return 1; // levelB è più critico
      return 0; // I valori sono uguali
    };
  }, []);

  /* eslint-disable react/prop-types */
  const columns = useMemo(() => {
    return [
      {
        Header: 'Elemento',
        accessor: (row) => {
          const { log, ip } = row.element;
          return `${log} (${ip})`;
        },
        Cell: ({ cell }) => {
          const { id, log, ip } = cell.row.original.element;
          return <ItemIdentified id={`${id}`} name={`${log} (${ip})`} />;
        },
        width: '30%',
        align: 'left',
        sortUndefined: 'last',
        disableSortBy: true,
      },
      {
        Header: 'Level',
        accessor: (row) => {
          return row.level;
        },
        Cell: ({ cell }) => {
          return (
            <MDBox ml={-1}>
              <MDBadge
                badgeContent={`${cell.value}`}
                color={mappingLevel(`${cell.value.toUpperCase()}`)}
                variant="gradient"
                size="sm"
              />
            </MDBox>
          );
        },
        align: 'center',
        sortUndefined: 'last',
        sortType: sortLevelFn,
      },
      {
        Header: 'Action Da/Data',
        accessor: (row) => {
          const { date, name } = row.date;
          return `${name} ${date}`;
        },
        Cell: ({ cell }) => {
          const { date, name } = cell.row.original.date;
          return <ItemIdentified id={`${date}`} name={`${name}`} />;
        },
        align: 'left',
        sortUndefined: 'last',
        disableSortBy: true,
      },
      {
        Header: 'Extra',
        accessor: 'extra',
        align: 'left',
        sortUndefined: 'last',
        disableSortBy: true,
      },
    ];
  }, [data.rows]);
  /* eslint-enable react/prop-types */

  const decorateFrontend = (dbData) => {
    if (!dbData || dbData.length <= 0) {
      return [];
    }

    const toFe = dbData.map((el) => {
      const who =
        typeof el?.context?.extra !== 'undefined' &&
        typeof el?.context?.extra['add-user'] !== 'undefined'
          ? el?.context?.extra['add-user']
          : el?.context?.extra?.user ?? 'N.A.';
      return {
        element: { id: el.id, log: el.message, ip: el.remote_addr },
        level: el.level_name,
        date: { date: formatDate(el.record_datetime), name: who },
        extra: (
          <MDTypography component="div" variant="caption" color="text" fontWeight="medium">
            <JsonView data={el.context} shouldInitiallyExpand={() => false} style={darkStyles} />
          </MDTypography>
        ),
      };
    });

    return toFe;
  };

  useEffect(() => {
    try {
      if (reupdate !== '') {
        getData().then((reqData) => {
          !!onupdate && typeof onupdate === 'function' && onupdate(reqData);
          if (!reqData?.code && isArray(reqData.logs)) {
            const mapped = decorateFrontend(reqData.logs);
            setData({
              columns,
              rows: mapped,
              fetching: false,
            });
          } else {
            setData({
              columns,
              rows: [],
              fetching: false,
            });
            console.warn('NO get log:', reqData);
          }
        });
      }
    } catch (e) {
      console.log(e);
      console.error(`getData: error on call: ${e.message()}`);
    }
    return () => console.log('LogDataTable unmounting...');
  }, [reupdate]);

  return (
    <DataTable
      table={{ columns: data.columns, rows: data.rows }}
      isSorted
      canSearch
      entriesPerPage={{ defaultValue: 20, entries: [5, 10, 20, 50, 100] }}
      showTotalEntries
      noEndBorder
      loading={data.rows.length <= 0 && !!data.fetching}
    />
  );
}

LogDataTable.defaultProps = {
  getData: () => {},
  reupdate: 'initial',
  onupdate: () => {},
};

LogDataTable.propTypes = {
  getData: PropTypes.func,
  reupdate: PropTypes.string,
  onupdate: PropTypes.func,
};

export default LogDataTable;
